import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, PackageX, Play, RefreshCw, Boxes, Pencil, Check } from "lucide-react";
import { sr } from "../../lib/sr";

export default function InventoryGuard() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [editing, setEditing] = useState(null); // link_id being edited
  const [sel, setSel] = useState([]);

  const load = () => {
    sr.guardCampaigns().then((r) => { setData(r.campaigns || []); setEnabled(!!r.enabled); }).catch(() => setData([]));
    sr.catalogItems({ type: "product" }).then((r) => setProducts(r.items || [])).catch(() => {});
    sr.guardSettings().then((r) => { setEnabled(!!r.enabled); setLastRun(r); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try { await sr.setGuardSettings(next); toast.success(next ? "Inventory guard on" : "Inventory guard off"); }
    catch { setEnabled(!next); toast.error("Could not update setting"); }
  };

  const runNow = async () => {
    setRunning(true);
    try { const r = await sr.runInventoryGuard(); toast.success(`Checked ${r.checked} · paused ${r.paused} · resumed ${r.resumed}`); setLastRun((p) => ({ ...(p || {}), last_result: r, last_run: new Date().toISOString() })); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run guard"); } finally { setRunning(false); }
  };

  const openEdit = (c) => { setEditing(c.link_id); setSel((c.products || []).map((p) => p.id)); };
  const saveEdit = async (linkId) => {
    setBusy(true);
    try { await sr.setGuardCampaignProducts(linkId, sel); toast.success("Linked products updated"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  const res = lastRun?.last_result;
  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="inventory-guard-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={22} /> Inventory-Aware Ads
        </h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--navy)", cursor: "pointer" }} data-testid="guard-toggle">
            <input type="checkbox" checked={enabled} onChange={toggle} data-testid="guard-toggle-input" />
            {enabled ? "Guard on" : "Guard off"}
          </label>
          <button className="sr-btn sr-btn-sm" onClick={load} data-testid="guard-refresh"><RefreshCw size={13} /></button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={runNow} disabled={running} data-testid="guard-run-now">
            {running ? <Loader2 size={13} className="sr-spin" /> : <><Play size={13} /> Run check now</>}
          </button>
        </div>
      </div>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        When <strong>every</strong> product a live campaign promotes runs out of stock, the campaign is paused automatically — and resumed the moment any restocks. You're notified the instant any single product goes out.
      </p>
      {res && (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", gap: 18, flexWrap: "wrap" }} data-testid="guard-last-run">
          {[["Checked", res.checked], ["Paused", res.paused], ["Resumed", res.resumed], ["Notified", res.notified]].map(([l, v]) => (
            <div key={l}><div className="sr-muted" style={{ fontSize: 11 }}>{l}</div><div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)" }}>{v ?? 0}</div></div>
          ))}
          {lastRun?.last_run && <div style={{ marginLeft: "auto", alignSelf: "center" }} className="sr-muted">Last run {new Date(lastRun.last_run).toLocaleString()}</div>}
        </div>
      )}

      {data.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 34 }} data-testid="guard-empty">
          <Boxes size={26} style={{ color: "var(--muted)" }} />
          <p style={{ marginTop: 8 }}>No live or paused campaigns yet. Publish a Meta campaign and link the products it promotes to protect it from out-of-stock spend.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {data.map((c) => (
            <div key={c.link_id} className="sr-card" data-testid={`guard-campaign-${c.link_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--navy)" }}>{c.name || "Untitled campaign"}</strong>
                  <span className={`sr-badge ${c.status === "ACTIVE" || c.status === "PUBLISHED" ? "sr-badge-green" : "sr-badge-amber"}`} data-testid={`guard-status-${c.link_id}`}>{c.status}</span>
                  {c.guard_paused && <span className="sr-badge sr-badge-amber" data-testid={`guard-paused-${c.link_id}`}><PackageX size={11} /> Paused · out of stock</span>}
                  {c.all_oos && c.status !== "PAUSED" && <span className="sr-badge sr-badge-amber">All promoted products out</span>}
                </div>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => (editing === c.link_id ? setEditing(null) : openEdit(c))} data-testid={`guard-edit-${c.link_id}`}>
                  <Pencil size={13} /> Linked products ({(c.products || []).length})
                </button>
              </div>
              {(c.products || []).length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {c.products.map((p) => (
                    <span key={p.id} className="sr-badge" style={{ background: p.oos ? "#FEE2E2" : "#ECFDF3", color: p.oos ? "#B42318" : "#067647" }} data-testid={`guard-product-${p.id}`}>
                      {p.name || "Product"}{p.tracked ? ` · ${p.inventory} in stock` : " · stock not tracked"}
                    </span>
                  ))}
                </div>
              )}
              {editing === c.link_id && (
                <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid={`guard-editor-${c.link_id}`}>
                  <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Pick the products this campaign promotes:</div>
                  {products.length === 0 ? <p className="sr-muted" style={{ fontSize: 12 }}>No products in your catalog yet.</p> : (
                    <div style={{ display: "grid", gap: 4, maxHeight: 220, overflowY: "auto" }}>
                      {products.map((p) => (
                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggleSel(p.id)} data-testid={`guard-pick-${p.id}`} />
                          <span>{p.name}</span>
                          <span className="sr-muted" style={{ fontSize: 11 }}>{typeof p?.details?.inventory === "number" ? `${p.details.inventory} in stock` : "stock not tracked"}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => saveEdit(c.link_id)} disabled={busy} data-testid={`guard-save-${c.link_id}`}>{busy ? <Loader2 size={13} className="sr-spin" /> : <><Check size={13} /> Save</>}</button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
